/* eslint-disable max-lines-per-function */
import { HttpErrorResponse } from '@angular/common/http';

import {
  PipelineParams,
  PipelineParamsWithStage,
  PipelinePlacement,
  PipelineStage,
} from '@recruitee/http/types';
import { createEntityCollectionStore, EntityState, pipeState } from '@recruitee/ngrx';

import { EntityStoreKey } from '../../types';

export interface PipelineStagesEntity {
  stages: PipelineStage[];
  id: string;
}

const updatePlacementsInStage = (
  stage: PipelineStage,
  newPlacement: PipelinePlacement,
): PipelinePlacement[] => {
  let placementsUpdated = false;
  const newPlacements = stage.placements.flatMap(placement => {
    if (newPlacement.id === placement.id) {
      placementsUpdated = true;
      // placement deleted or moved to another stage
      if (newPlacement.stageId !== placement.stageId) {
        return [];
      }
      return newPlacement;
    } else {
      return placement;
    }
  });

  return placementsUpdated ? newPlacements : stage.placements;
};

const addPlacementToStage = (
  stage: PipelineStage,
  newPlacements: PipelinePlacement[],
  newPlacement: PipelinePlacement,
): PipelinePlacement[] =>
  [...newPlacements, newPlacement].sort((a, b) => {
    if (a.position === b.position) {
      return a.updatedAt < b.updatedAt ? -1 : 1;
    }

    return a.position < b.position ? 1 : -1;
  });

const updatePlacement = (
  state: EntityState<PipelineStagesEntity>,
  offerSlug: string,
  newPlacement: PipelinePlacement,
): PipelineStagesEntity => {
  const stages = state.entityMap[offerSlug]?.stages || [];
  return {
    id: offerSlug,
    stages: stages.map(stage => {
      const updatedPlacements = updatePlacementsInStage(stage, newPlacement);
      const placementsUpdated = updatedPlacements !== stage.placements;

      if (!placementsUpdated && newPlacement.stageId === stage.id) {
        return {
          ...stage,
          placements: addPlacementToStage(stage, updatedPlacements, newPlacement),
        };
      }

      return {
        ...stage,
        placements: updatedPlacements,
      };
    }),
  };
};

const updateStage = (
  state: EntityState<PipelineStagesEntity>,
  offerSlug: string,
  stageId: number,
  newPlacements: PipelinePlacement[],
): PipelineStagesEntity => {
  const stages = state.entityMap[offerSlug]?.stages || [];
  return {
    id: offerSlug,
    stages: stages.map(stage => {
      if (stage.id === stageId) {
        return { ...stage, placements: newPlacements };
      }

      return stage;
    }),
  };
};

const deletePlacements = (
  state: EntityState<PipelineStagesEntity>,
  payload: { offerSlug: string; candidateIds: number[] },
): PipelineStagesEntity => {
  const stages = state.entityMap[payload.offerSlug]?.stages!;
  const candidateIdsMap = Object.fromEntries(payload.candidateIds.map(id => [id, true]));

  return {
    id: payload.offerSlug,
    stages: stages.map(stage => ({
      ...stage,
      placements: stage.placements.filter(placement => !candidateIdsMap[placement.candidateId]),
    })),
  };
};

const {
  actions: defaultActions,
  reducer,
  namespace,
  selectors: { byId },
} = createEntityCollectionStore<PipelineStagesEntity>()(EntityStoreKey.PipelineStages, {
  customActions: adapter => ({
    fetchOne: (state, payload: { id: string; params: PipelineParams }) =>
      pipeState(state, next =>
        adapter.markAsPending(next, { scope: null, entities: [payload.id] }),
      ),
    updatePlacement: (
      state,
      payload: { offerId: number; offerSlug: string; candidateId: number; params: PipelineParams },
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.offerSlug] }),
    updatePlacementSuccess: (state, payload: { offerSlug: string; data: PipelinePlacement }) => {
      const result = updatePlacement(state, payload.offerSlug, payload.data);
      return pipeState(
        state,
        next => adapter.updateOne(next, result),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.offerSlug] }),
      );
    },
    updatePlacementFailed: (state, payload: { offerSlug: string; error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.offerSlug] }),
    updateStagePlacements: (
      state,
      payload: { offerSlug: string; params: PipelineParamsWithStage },
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.offerSlug] }),
    updateStagePlacementsSuccess: (
      state,
      payload: { offerSlug: string; stageId: number; placements: PipelinePlacement[] },
    ) => {
      const result = updateStage(state, payload.offerSlug, payload.stageId, payload.placements);
      return pipeState(
        state,
        next => adapter.updateOne(next, result),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.offerSlug] }),
      );
    },
    updateStagePlacementsFailed: (
      state,
      payload: { offerSlug: string; error: HttpErrorResponse },
    ) => adapter.setError(state, payload.error, { scope: null, entities: [payload.offerSlug] }),
    deletePlacements: (state, payload: { offerSlug: string; candidateIds: number[] }) =>
      adapter.updateOne(state, deletePlacements(state, payload)),
  }),
});

const actions = {
  fetchOne: defaultActions.fetchOne,
  fetchOneSuccess: defaultActions.fetchOneSuccess,
  fetchOneFailed: defaultActions.fetchOneFailed,
  updatePlacement: defaultActions.updatePlacement,
  updatePlacementSuccess: defaultActions.updatePlacementSuccess,
  updatePlacementFailed: defaultActions.updatePlacementFailed,
  updateStagePlacements: defaultActions.updateStagePlacements,
  updateStagePlacementsSuccess: defaultActions.updateStagePlacementsSuccess,
  updateStagePlacementsFailed: defaultActions.updateStagePlacementsFailed,
  deletePlacements: defaultActions.deletePlacements,
};
const PipelineStagesSelectors = { byId };

export {
  actions as PipelineStagesActions,
  reducer as PipelineStagesReducer,
  namespace as PipelineStagesNamespace,
  PipelineStagesSelectors,
};
